import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createExpense, getExpenseCategories, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

const PAYMENT_METHODS = ["cash", "card", "bank"];

export default function AddExpense() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("accounting");

  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    category_id: "",
    amount: "",
    payment_method: "cash",
    expense_date: new Date().toISOString().slice(0, 10),
    description: "",
    branch_id: "",
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getExpenseCategories(token).then((all) => setCategories(all.filter((c) => c.is_active))).catch(() => notify.error(t("notify_load_categories_failed", "Failed to load expense categories")));
    getBranches(token).then(setBranches).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }));
  const paymentMethodOptions = PAYMENT_METHODS.map((m) => ({ value: m, label: t(`payment_method_${m}`, m) }));
  const branchOptions = [{ value: "", label: t("field_branch_none_option", "No specific branch") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      setReceiptPreview(file.type === "application/pdf" ? null : URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.category_id) newErrors.category_id = t("validation_category_required", "A category is required");
    if (!form.amount || Number(form.amount) <= 0) newErrors.amount = t("validation_amount_required", "Enter an amount greater than zero");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    try {
      await createExpense(token, {
        category_id: form.category_id,
        amount: form.amount,
        payment_method: form.payment_method,
        expense_date: form.expense_date,
        description: form.description.trim(),
        branch_id: form.branch_id,
        receipt: receiptFile || undefined,
      });
      notify.success(t("notify_expense_created", "Expense recorded and posted successfully"));
      navigate("/expenses");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/expenses")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("add_expense_title", "Record Expense")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("add_expense_subtitle", "Posts its own journal entry automatically — this can't be edited later, only voided")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectBox
              label={t("field_category_label", "Category")}
              options={categoryOptions}
              value={form.category_id}
              onChange={(val) => setForm((prev) => ({ ...prev, category_id: val }))}
              error={errors.category_id}
            />
            <FloatingInput label={t("field_amount_label", "Amount")} type="number" value={form.amount} onChange={setField("amount")} error={errors.amount} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectBox
              label={t("field_payment_method_label", "Payment Method")}
              options={paymentMethodOptions}
              value={form.payment_method}
              onChange={(val) => setForm((prev) => ({ ...prev, payment_method: val }))}
            />
            <FloatingInput label={t("field_expense_date_label", "Date")} type="date" value={form.expense_date} onChange={setField("expense_date")} />
          </div>

          <SelectBox
            label={t("field_branch_label", "Branch (optional)")}
            options={branchOptions}
            value={form.branch_id}
            onChange={(val) => setForm((prev) => ({ ...prev, branch_id: val }))}
          />

          <FloatingInput label={t("field_expense_description_label", "Description (optional)")} value={form.description} onChange={setField("description")} />

          <div>
            <label className="text-xs font-bold uppercase text-gray-500 mb-2 block">{t("field_receipt_label", "Receipt (optional — image or PDF)")}</label>
            <div className="flex items-center gap-4">
              {receiptPreview && (
                <img src={receiptPreview} alt="Receipt preview" className="w-16 h-16 rounded-xl object-cover border border-gray-100" />
              )}
              {receiptFile && !receiptPreview && (
                <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-[10px] text-gray-400 font-bold uppercase">PDF</div>
              )}
              <label className="cursor-pointer">
                <span className="inline-flex items-center gap-2 px-4 h-10 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                  <ICONS.Install className="w-4 h-4" />
                  {receiptFile ? t("btn_change_receipt", "Change File") : t("btn_upload_receipt", "Upload File")}
                </span>
                <input type="file" className="hidden" onChange={handleReceiptChange} accept="image/*,.pdf" />
              </label>
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={ICONS.Save}
            >
              {t("btn_record_expense", "Record Expense")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/expenses")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
